/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { DataGrid } from 'labstep-web/core/DataGrid';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import { TextLink } from 'labstep-web/core/DataGrid/Text/Link';
import type {
  CellEditorParams,
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceShowCenterSecondaryInfo } from 'labstep-web/screens/Resource/Show/Center/SecondaryInfo';
export const getProtocolValueResourceColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
): SmartTableColDef => ({
  colId: `resource`,
  sortable: true,
  headerName: 'Resource',
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: (params: HeaderComponentParamsProps) => {
    return {
      toolPanelColumnName: `Resource`,
      icon: ICONS.resource.primary,
      experimentOrProtocol,
    };
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolValueService.getEditable(params),
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }

    const protocolValue = params.data as ProtocolValue;

    const resourceItem = protocolValue?.is_input
      ? protocolValue?.resourceItem
      : protocolValue?.resource_item_output;

    let resource = null;

    if (resourceItem) {
      resource = resourceItem.resource;
    }

    if (protocolValue?.resource) {
      resource = protocolValue.resource;
    }

    if (!resource) {
      return '';
    }

    return resource.guid;
  },
  cellRenderer: (params: CellRendererParams<ProtocolValue>) => {
    const protocolValue = params.data as ProtocolValue;

    const resourceItem = protocolValue?.is_input
      ? protocolValue?.resourceItem
      : protocolValue?.resource_item_output;

    let resource = null;

    if (resourceItem) {
      resource = resourceItem.resource;
    }

    if (protocolValue?.resource) {
      resource = protocolValue.resource;
    }

    if (!resource) {
      return (
        <span>
          <DataGridPlaceholder
            children={'Select'}
            params={params}
            editable={GridSmartTableProtocolValueService.getEditable(
              params,
            )}
          />
        </span>
      );
    }

    return (
      <div>
        <TextLink
          entity={resource}
          entityPreviewProps={{
            secondaryInfo: (
              <ResourceShowCenterSecondaryInfo resource={resource} />
            ),
          }}
          disabled
        />
      </div>
    );
  },
  cellEditor: DataGrid?.CellEditor?.SearchSelectModal,
  cellEditorParams: (params: CellEditorParams<ProtocolValue>) => {
    if (!params.node) {
      return false;
    }

    return false;
  },
  valueSetter: (params) => {
    const newValue = params.newValue;
    if (
      !params.node ||
      newValue === '' ||
      params.newValue === params.oldValue
    ) {
      return false;
    }

    const protocolValue = params.data as ProtocolValue;

    if (protocolValue) {
      return GridSmartTableProtocolValueService.updateEntity(
        params,
        protocolValue.entityName,
        protocolValue.idAttr,
        {
          resource_id: newValue,
        },
      );
    } else {
      return GridSmartTableProtocolValueService.createEntity(
        params,
        ProtocolValue.entityName,
        {
          resource_id: newValue,
          protocol_condition_id: params.data.guid,
          [`${experimentOrProtocol.entityName}_id`]:
            experimentOrProtocol.guid,
        },
      );
    }
  },
  // FIXME
  // cellStyle: (params) =>
  //   GridSmartTableProtocolValueService.getCellStyle(
  //     params,
  //     experimentOrProtocol,
  //     ProtocolValue.entityName,
  //     name,
  //   ),
  valueClearer: () => {
    return null;
  },
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
});
